import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useT } from "../i18n";

const FLOWER_ICON = "https://customer-assets-7cd3h4nn.emergentagent.net/job_martial-arts-refresh/artifacts/zs3ikesz_Flower.png.webp";

const Home = () => {
  const { lang } = useLanguage();
  const t = useT(lang);

  return (
    <div>
      {/* HERO — split: text left, Sifu portrait right */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center bg-[#0A0A0A]">
        {/* Sifu photo — right half on desktop, background on mobile */}
        <div className="absolute inset-y-0 right-0 w-full md:w-[62%]">
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_martial-arts-refresh/artifacts/d8mswe4n_Home%20Page.png"
            alt="Sifu Νίκος Αγαθόπουλος"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center" }}
          />
          {/* Soft left-side fade so photo blends into dark half */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent md:from-[#0A0A0A] md:via-transparent md:to-transparent" />
          {/* Mobile-only dark overlay so text is legible */}
          <div className="absolute inset-0 bg-[#0A0A0A]/60 md:hidden" />
        </div>

        <div className="absolute right-[-8%] top-[-4%] cn-bg z-0">詠春</div>

        <div className="container-wc relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 py-24">
          <div className="lg:col-span-8 fade-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="divider-gold" />
              <span className="eyebrow">{t.hero.badge}</span>
            </div>
            <h1 className="h-display text-5xl md:text-7xl lg:text-8xl mb-6" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}>
              {t.hero.title}
              <span className="block text-gold italic font-serif text-3xl md:text-5xl mt-4" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}>
                — {t.hero.subtitle}
              </span>
            </h1>
            <p className="text-dim text-lg max-w-2xl leading-relaxed mb-10" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.8)" }}>
              {t.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                {t.hero.cta_primary} <ArrowRight size={16} />
              </Link>
              <Link to="/sifu" data-testid="hero-cta-secondary" className="btn-outline">
                {t.hero.cta_secondary}
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-0 right-0 container-wc flex items-center justify-between text-xs text-dim">
          <span className="tracking-[0.3em] uppercase">Est. 2011</span>
          <span className="font-cn text-gold">師傅</span>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 md:py-32">
        <div className="container-wc">
          <div className="max-w-3xl mb-16">
            <div className="flex items-center gap-3 mb-4">
              <span className="divider-gold" />
              <span className="eyebrow">{t.home.pillars_title}</span>
            </div>
            <h2 className="h-display text-4xl md:text-5xl">
              {lang === "el" ? "Τρεις πυλώνες, ένας δρόμος." : "Three pillars, one path."}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { t: t.home.pillar1_title, d: t.home.pillar1_text, num: "01" },
              { t: t.home.pillar2_title, d: t.home.pillar2_text, num: "02" },
              { t: t.home.pillar3_title, d: t.home.pillar3_text, num: "03" },
            ].map((p, i) => (
              <div key={p.num} data-testid={`pillar-${i}`} className="card group">
                <div className="flex items-start justify-between mb-8">
                  <img
                    src={FLOWER_ICON}
                    alt=""
                    aria-hidden="true"
                    className="w-12 h-12 object-contain transition-transform duration-500 group-hover:rotate-45"
                  />
                  <span className="font-serif text-5xl text-primary opacity-40 leading-none">{p.num}</span>
                </div>
                <h3 className="font-serif text-2xl mb-3">{p.t}</h3>
                <p className="text-dim text-sm leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-surface">
        <div className="absolute left-[-10%] top-[-20%] cn-bg" style={{ writingMode: "horizontal-tb" }}>道</div>
        <div className="container-wc grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="divider-gold" />
              <span className="eyebrow">{lang === "el" ? "Φιλοσοφία" : "Philosophy"}</span>
            </div>
            <h2 className="h-display text-4xl md:text-5xl mb-6">{t.home.philosophy_title}</h2>
            <p className="text-dim leading-relaxed text-lg mb-8">{t.home.philosophy_text}</p>
            <blockquote className="border-l-2 border-gold pl-6 my-8">
              <p className="font-serif italic text-2xl md:text-3xl text-[#F5F5F0] leading-snug mb-3">
                &ldquo;{t.home.quote}&rdquo;
              </p>
              <cite className="text-gold text-sm not-italic tracking-widest uppercase">— {t.home.quote_author}</cite>
            </blockquote>
            <Link to="/sifu" data-testid="philosophy-cta" className="btn-outline">
              {lang === "el" ? "Γνωρίστε τον Sifu" : "Meet the Sifu"} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative">
            <div className="aspect-[3/4] overflow-hidden border border-goldSoft">
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_martial-arts-refresh/artifacts/xabqejl3_%CE%A6%CE%B9%CE%BB%CE%BF%CF%83%CE%BF%CF%86%CE%B9%CE%B1.webp"
                alt="Bruce Lee"
                className="w-full h-full object-cover img-moody"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-primary p-6 border border-gold hidden md:block">
              <span className="font-cn text-gold text-5xl leading-none">詠春</span>
            </div>
          </div>
        </div>
      </section>

      {/* CLASSES TEASER */}
      <section className="py-24 md:py-32">
        <div className="container-wc">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-4">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="divider-gold" />
                <span className="eyebrow">{t.home.classes_title}</span>
              </div>
              <h2 className="h-display text-4xl md:text-5xl">
                {lang === "el" ? "Έξι τρόποι εκπαίδευσης." : "Six ways to train."}
              </h2>
            </div>
            <Link to="/classes" data-testid="home-view-all-classes" className="btn-outline">
              {t.home.view_all} <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.classes.list.map((c, i) => (
              <div key={c.t} data-testid={`class-card-${i}`} className="card">
                <span className="text-gold text-xs tracking-[0.3em]">0{i + 1}</span>
                <h3 className="font-serif text-2xl mt-3 mb-3">{c.t}</h3>
                <p className="text-dim text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-60">
          <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_martial-arts-refresh/artifacts/ffl5mekd_%CE%9F%20%CE%B4%CF%81%CE%BF%CE%BC%CE%BF%CF%82%20%CE%BE%CE%B5%CE%BA%CE%B9%CE%BD%CE%B1%20%CE%BC%CE%B5%20%CE%B5%CE%BD%CE%B1%20%CE%B2%CE%B7%CE%BC%CE%B1.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0A0A]/70" />
        </div>
        <div className="container-wc relative text-center max-w-3xl mx-auto">
          <span className="font-cn text-gold text-6xl block mb-6">武</span>
          <h2 className="h-display text-4xl md:text-6xl mb-6">
            {lang === "el" ? "Ο δρόμος ξεκινά με ένα βήμα." : "The path starts with one step."}
          </h2>
          <p className="text-dim text-lg mb-10">
            {lang === "el" ? "Κλείστε το εισαγωγικό σας μάθημα σήμερα." : "Book your introductory lesson today."}
          </p>
          <Link to="/contact" data-testid="footer-cta" className="btn-primary">
            {t.hero.cta_primary} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
